import { Button } from '@strata/react-buttons';
import { WarningDialog } from '@strata/react-dialog';
import { Grid } from '@strata/react-grid';
import { Hyperlink } from '@strata/react-hyperlink';
import { DetailValue } from '@strata/react-label-value-pairs';
import { ErrorToast, SuccessToast } from '@strata/react-toast-message';
import PropTypes from 'prop-types';
import { useState } from 'react';

import { handleError } from 'services/ErrorManagement';
import { httpDelete } from 'services/Http';
import {
    ListSchema,
    ListSchemaApiURLPrefix,
    ListTypeNameMap,
} from 'services/ListManagementHelpers';
import { routeRulesUrl } from 'services/RouteRuleHelpers';

const columns = [
    {
        accessorKey: 'route_name',
        header: 'Route Rules',
        cell: ({ getValue, row }) => (
            <Hyperlink
                linkText={getValue()}
                navUrl={`${routeRulesUrl(row.original.segment_id, row.original.benefit)}/${
                    row.original.route_rule_id
                }`}
            />
        ),
    },
];

export const DeleteListDialog = ({ listSchema, listType, listToDelete = undefined, onClose }) => {
    const [rulesUsingList, setRulesUsingList] = useState();
    const hasRulesUsingList = rulesUsingList?.length > 0;
    const listTypeName = ListTypeNameMap[listType];
    return (
        <WarningDialog
            dialogTitle={!hasRulesUsingList ? `Confirm Delete` : `Delete Warning`}
            width={600}
            isOpen={!!listToDelete}
            primaryButtonLabel={
                !hasRulesUsingList ? `Yes, Delete ${listTypeName} List` : 'Retry Delete'
            }
            primaryButtonIntent={!hasRulesUsingList ? Button.Intent.DANGER : Button.Intent.PRIMARY}
            onPrimaryButtonClick={async () => {
                try {
                    const result = await httpDelete(
                        `${ListSchemaApiURLPrefix[listSchema]}/${listType}/${listToDelete?.[`${listType}_list_id`]}`,
                    );

                    if (result?.length > 0) {
                        ErrorToast({
                            message: `${listToDelete?.list_name} list cannot be deleted because it is used in route rules. See dialog below for details.`,
                        });
                        setRulesUsingList(result);
                    } else {
                        SuccessToast({
                            message: `${listToDelete?.list_name} successfully deleted.`,
                        });
                        onClose();
                        setRulesUsingList();
                    }
                } catch (err) {
                    handleError(`Error deleting ${listToDelete?.list_name}.`, err);
                }
            }}
            secondaryButtonLabel={!hasRulesUsingList ? `No, Cancel` : 'Cancel'}
            onSecondaryButtonClick={() => {
                onClose();
                setRulesUsingList();
            }}
            dialogContent={
                <>
                    <div className="margin__bottom--large">
                        {!hasRulesUsingList
                            ? `Are you sure you want to delete the following ${listTypeName} List?`
                            : `This ${listTypeName} List cannot be deleted because it is used in route rules. When you have removed this list from the following route rules you can click Retry Delete.`}
                    </div>
                    <div className="margin__bottom--small">
                        <DetailValue value={listToDelete?.list_name} />
                    </div>
                    {hasRulesUsingList && (
                        <div className="margin__bottom--large">
                            <Grid data={rulesUsingList} columns={columns} />
                        </div>
                    )}
                </>
            }
        />
    );
};

DeleteListDialog.propTypes = {
    listSchema: PropTypes.oneOf(Object.values(ListSchema)).isRequired,
    listType: PropTypes.string.isRequired,
    listToDelete: PropTypes.shape({
        list_name: PropTypes.string.isRequired,
    }),
    onClose: PropTypes.func.isRequired,
};
